import { Box } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useIsFeatureEnabled } from "#app/featureToggle";
import { showV2CompactToast, showV2Toast } from "#app/notifications";
import { spacing } from "#app/theme/v2/spacing";
import { Button } from "#components/atoms/Button/Button";
import { BulkActionBar } from "#components/molecules/BulkActionBar/BulkActionBar";
import { BulkActionBarOverlay } from "#components/molecules/BulkActionBar/BulkActionBarOverlay";
import { Table, type TableHandle } from "#components/organisms/Table/Table";
import {
  getCustomMaterialsQueryData,
  setCustomMaterialsQueryData,
} from "#modules/materials/infra/customMaterials.query";
import { Material } from "#modules/materials/infra/fetchCustomMaterials.api";
import { UpdateMaterialDto } from "#modules/materials/infra/updateMaterial.api";
import { useBulkDeleteMaterials } from "#modules/materials/view/useBulkDeleteMaterials";
import { useCustomMaterials } from "#modules/materials/view/useCustomMaterials";
import { useDeleteMaterial } from "#modules/materials/view/useDeleteMaterial";
import { useMaterialStructureImport } from "#modules/materials/view/useMaterialStructureImport";
import { useUpdateMaterial } from "#modules/materials/view/useUpdateMaterial";
import { MaterialSpecificationEmptyState } from "#modules/onboardingV2/view/materialSpecification/MaterialSpecificationEmptyState";
import { useDeletableMaterialRows } from "#modules/onboardingV2/view/materialSpecification/useDeletableMaterialRows";
import { OnboardingV2Shell } from "#modules/onboardingV2/view/OnboardingV2Shell";
import { useAddDefaultOnboardingProcessSteps } from "#modules/onboardingV2/view/processStepConfiguration/useAddDefaultOnboardingProcessSteps";
import { AddRawMaterialModal } from "#modules/onboardingV2/view/rawMaterialSpecification/AddRawMaterialModal";
import { ImportMaterialListButton } from "#modules/onboardingV2/view/rawMaterialSpecification/ImportMaterialListButton";
import { MaterialImportAlert } from "#modules/onboardingV2/view/rawMaterialSpecification/MaterialImportAlert";
import { useRawMaterialSpecificationColumns } from "#modules/onboardingV2/view/rawMaterialSpecification/useRawMaterialSpecificationColumns";
import { useOnboardingActivityTypeLabel } from "#modules/onboardingV2/view/useOnboardingActivityTypeLabel";
import {
  useOnboardingAnalytics,
  useTrackOnboardingStepViewed,
} from "#modules/onboardingV2/view/useOnboardingAnalytics";
import { useOnboardingStepNavigation } from "#modules/onboardingV2/view/useOnboardingStepNavigation";
import { useUndoableBulkDelete } from "#modules/onboardingV2/view/useUndoableBulkDelete";
import { Icon } from "#shared/icons/Icon";
import { AnalyticsEvent } from "#shared/useAnalytics";
import { Flex } from "#shared/view/components/Flex";
import { Features } from "#types/FeatureToggleTypes";
import { findDuplicateRawMaterialIds, MaterialStructureImportResult } from "shared";

export const OnboardingRawMaterialSpecificationStepPage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { position, total, goToNext, goToPrevious } = useOnboardingStepNavigation(
    "rawMaterialSpecification",
  );
  const { customMaterials, isLoading } = useCustomMaterials({});
  const { updateMaterial } = useUpdateMaterial();
  const { deleteMaterial } = useDeleteMaterial();
  const { bulkDeleteMaterials } = useBulkDeleteMaterials();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [hasCheckedDuplicates, setHasCheckedDuplicates] = useState(false);
  const tableRef = useRef<TableHandle>(null);
  const [isAddModalOpened, { open: openAddModal, close: closeAddModal }] = useDisclosure(false);

  useTrackOnboardingStepViewed("rawMaterialSpecification");
  const { captureOnboardingEvent } = useOnboardingAnalytics("rawMaterialSpecification");

  const isMaterialListImportEnabled = useIsFeatureEnabled(Features.MATERIAL_LIST_IMPORT);
  const [materialImportResult, setMaterialImportResult] =
    useState<MaterialStructureImportResult | null>(null);

  const handleMaterialImportFinished = (result: MaterialStructureImportResult) => {
    captureOnboardingEvent(AnalyticsEvent.OnboardingMaterialListImported, {
      outcome: result.outcome,
      ...(result.outcome === "failed"
        ? { failureCode: result.failureCode }
        : {
            importedCount: result.importedCount,
            skippedCount: result.skipped.length,
            warningCount: result.warnings.length,
          }),
    });

    const isCleanImport =
      result.outcome === "imported" &&
      result.skipped.length === 0 &&
      result.warnings.length === 0 &&
      result.priceAlerts.length === 0;

    if (isCleanImport) {
      setMaterialImportResult(null);
      showV2CompactToast({
        message: t("onboardingV2.materialImport.success", { count: result.importedCount }),
      });
      return;
    }

    setMaterialImportResult(result);
  };

  const { importMaterialList, isImporting } = useMaterialStructureImport({
    onFinished: handleMaterialImportFinished,
  });

  const handleImportMaterialList = (file: File) => {
    setMaterialImportResult(null);
    setHasCheckedDuplicates(false);
    importMaterialList(file);
  };

  const handleOpenAddModal = () => {
    captureOnboardingEvent(AnalyticsEvent.OnboardingAddMaterialClicked);
    openAddModal();
  };

  const handleClearSelection = () => {
    tableRef.current?.resetRowSelection();
    setSelectedIds([]);
  };

  const { bulkDelete, commitPendingBulkDelete } = useUndoableBulkDelete({
    getRowId: (row) => row.id,
    getData: () => getCustomMaterialsQueryData(queryClient),
    setData: (rows) => setCustomMaterialsQueryData(queryClient, rows),
    onCommit: (ids) => bulkDeleteMaterials({ ids }),
    showUndoToast: ({ id, count, onCancel }) =>
      showV2Toast({
        id,
        message: t("onboardingV2.materialSpecification.bulkDelete.toast", {
          count,
        }),
        onCancel,
      }),
    onUndo: () => {
      handleClearSelection();
      showV2CompactToast({
        message: t("onboardingV2.materialSpecification.bulkDelete.undoToast"),
      });
    },
  });

  const handleUpdateMaterial = (id: string, payload: UpdateMaterialDto) => {
    updateMaterial({ id, payload });
  };

  const { visibleRows, deleteRow, commitPendingRowDeletes } = useDeletableMaterialRows<Material>({
    rows: customMaterials,
    getRowId: (material) => material.id,
    getDeleteToastMessage: (count) =>
      t("onboardingV2.materialSpecification.deleteToast.message", { count }),
    getUndoToastMessage: (count) =>
      t("onboardingV2.materialSpecification.undoToast.message", { count }),
    onConfirmDelete: (material) => deleteMaterial({ id: material.id }),
    onConfirmBulkDelete: (materials) =>
      bulkDeleteMaterials({ ids: materials.map((material) => material.id) }),
  });

  const duplicateIds = useMemo(
    () =>
      hasCheckedDuplicates ? new Set(findDuplicateRawMaterialIds(visibleRows)) : new Set<string>(),
    [hasCheckedDuplicates, visibleRows],
  );

  const columns = useRawMaterialSpecificationColumns(handleUpdateMaterial, deleteRow);

  const activityType = useOnboardingActivityTypeLabel();

  const { addDefaultOnboardingProcessSteps, isPending: isAddingDefaultProcessSteps } =
    useAddDefaultOnboardingProcessSteps();

  const handleBulkDelete = () => {
    bulkDelete(selectedIds);
    setSelectedIds([]);
  };

  const handleContinue = async () => {
    if (isAddingDefaultProcessSteps) {
      return;
    }

    if (!hasCheckedDuplicates) {
      if (findDuplicateRawMaterialIds(visibleRows).length > 0) {
        setHasCheckedDuplicates(true);
        return;
      }
    } else if (duplicateIds.size > 0) {
      return;
    }

    try {
      await addDefaultOnboardingProcessSteps();
    } catch {
      return;
    }

    commitPendingBulkDelete();
    commitPendingRowDeletes();
    goToNext();
  };

  return (
    <OnboardingV2Shell size="lg">
      <OnboardingV2Shell.Header
        onBack={goToPrevious}
        step={position}
        total={total}
        title={t("onboardingV2.rawMaterialSpecification.title")}
        subtitle={t("onboardingV2.rawMaterialSpecification.subtitle", {
          activityType,
        })}
      />

      <Box
        style={{
          position: "relative",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <OnboardingV2Shell.Content>
          {materialImportResult && (
            <MaterialImportAlert
              result={materialImportResult}
              materials={customMaterials}
              onClose={() => setMaterialImportResult(null)}
            />
          )}
          <Table
            ref={tableRef}
            data={visibleRows}
            columns={columns}
            getRowId={(row) => row.id}
            getRowError={(row) => duplicateIds.has(row.id)}
            loading={isLoading || isImporting}
            emptyState={<MaterialSpecificationEmptyState onAddMaterial={handleOpenAddModal} />}
            onRowSelectionChange={setSelectedIds}
          />
        </OnboardingV2Shell.Content>
        <BulkActionBarOverlay visible={selectedIds.length > 0}>
          <BulkActionBar
            label={t("onboardingV2.materialSpecification.bulkDelete.selectedCount", {
              count: selectedIds.length,
            })}
            clearAriaLabel={t("onboardingV2.materialSpecification.bulkDelete.clearSelection")}
            onClear={handleClearSelection}
            actions={[
              {
                key: "delete",
                icon: <Icon name="trash" size={16} />,
                label: t("common.delete"),
                onClick: handleBulkDelete,
              },
            ]}
          />
        </BulkActionBarOverlay>
      </Box>

      <OnboardingV2Shell.Footer
        buttonLabel={t("onboardingV2.continue")}
        onContinue={handleContinue}
        loading={isAddingDefaultProcessSteps}
        errorMessage={
          hasCheckedDuplicates && duplicateIds.size > 0
            ? t("onboardingV2.duplicateMaterialsError")
            : undefined
        }
        stepper={{ total, current: position }}
        leftAction={
          <Flex gap={spacing[2]}>
            <Button
              variant="secondary"
              size="md"
              leftSection={<Icon name="plus" size={16} />}
              onClick={handleOpenAddModal}
            >
              {t("onboardingV2.rawMaterialSpecification.addMaterial")}
            </Button>
            {isMaterialListImportEnabled && (
              <ImportMaterialListButton
                existingMaterialCount={customMaterials.length}
                loading={isImporting}
                onImport={handleImportMaterialList}
              />
            )}
          </Flex>
        }
      />

      <AddRawMaterialModal opened={isAddModalOpened} onClose={closeAddModal} />
    </OnboardingV2Shell>
  );
};
